import styled from "@emotion/styled";
import { css } from "@styled-system/css";
import { darken } from "polished";
import { compose, space, SpaceProps, variant } from "styled-system";

type Props = {
  align?: "left" | "right" | "center";
  size?: "s" | "m" | "l" | "xxl";
  use?: "default" | "base" | "white";
  weight?: "default" | "thin";
  wide?: "fill" | "half";
};

export const Button = styled("button")<Props & SpaceProps>(
  ({ theme }) =>
    css({
      position: "relative",
      border: theme.borders.default,
      cursor: "pointer",
      outline: 0,
      fontWeight: theme.fontWeights.bold,
      transition: theme.transitions.default,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      whiteSpace: "nowrap",
      userSelect: "none",
      borderRadius: theme.radii.default,
      "&:hover": {
        boxShadow: theme.shadows.default,
      },
      "&:active": {
        background: theme.colors.background.bg,
      },
      "&:disabled": {
        cursor: "not-allowed",
        opacity: 0.5,
        pointerEvents: "none",
        "&:hover": {
          boxShadow: "none",
        },
      },
    }),
  ({ theme }) =>
    compose(
      space,
      variant({
        prop: "align",
        variants: {
          left: css({
            textAlign: "left",
          }),
          center: css({
            textAlign: "center",
          }),
          right: css({
            textAlign: "right",
          }),
        },
      }),
      variant({
        prop: "size",
        variants: {
          s: css({
            padding: `${theme.space.s} ${theme.space.l}`,
            fontSize: theme.fontSizes.xs,
          }),
          m: css({
            padding: `${theme.space.m} ${theme.space.l}`,
            fontSize: theme.fontSizes.s,
          }),
          l: css({
            padding: `${theme.space.m} ${theme.space.xl}`,
            fontSize: theme.fontSizes.l,
            [`@media ${theme.mediaQueries.tablet}`]: {
              fontSize: theme.fontSizes.m,
            },
          }),
          xxl: css({
            padding: `${theme.space.xxl} ${theme.space.l}`,
            fontSize: theme.fontSizes.l,
          }),
        },
      }),
      variant({
        prop: "use",
        variants: {
          default: css({
            color: theme.colors.text.default,
            background: theme.colors.background.default,
            "&:disabled": {
              backgroundColor: theme.colors.background.bg,
              borderColor: "transparent",
            },
          }),
          base: css({
            color: theme.colors.text.white,
            background: theme.colors.background.black,
            border: theme.borders.transparent,
            "&:active": {
              background: darken(0.05, theme.colors.background.black),
            },
            "&:disabled": {
              color: theme.colors.text.default,
              background: theme.colors.background.bg,
            },
          }),
          white: css({
            color: theme.colors.text.default,
            background: theme.colors.background.transparent,
            border: theme.borders.transparent,
            "&:hover": {
              boxShadow: "none",
              background: theme.colors.background.bg,
            },
            "&:active": {
              boxShadow: "none",
            },
          }),
        },
      }),
      variant({
        prop: "weight",
        variants: {
          default: css({
            fontWeight: theme.fontWeights.bold,
          }),
          thin: css({
            fontWeight: theme.fontWeights.default,
          }),
        },
      }),
      variant({
        prop: "wide",
        variants: {
          fill: css({
            width: "100%",
            paddingLeft: 0,
            paddingRight: 0,
          }),
          half: css({
            width: "49%",
            paddingLeft: "0",
            paddingRight: "0",
          }),
        },
      }),
    ),
);

Button.defaultProps = {
  size: "m",
  use: "default",
};
